import {
  EAlignTypes,
  EJustifyTypes,
  EProblemReportSortBy,
  EProblemReportSortOrder,
  EProblemReportStatus
} from '@constants';
import {
  IActivity,
  IReport,
  ProblemReportRow,
  ProblemReportsTableSortConfig
} from '@sdk/contracts';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { TFunction } from 'i18next';
import { AlignType } from 'rc-table/lib/interface';

import { formatRelativeDate } from '../../../utils/formatRelativeDate';
import { getSortOrder, getSortTooltip } from '../../../utils/sortHelpers';
import { MoreActions } from '../../MoreActions';
import { LastReportDateCell, TypeCell } from '../Cells';

import { Counter, TotalReportsColumHeader } from './columns.styles';

import { Badge } from '@/components/Badge';

type StatusCountColumnConfig = {
  status: EProblemReportStatus;
  sortByKey: EProblemReportSortBy;
  sortBy?: EProblemReportSortBy;
  sortOrder?: EProblemReportSortOrder;
  sortConfig?: ProblemReportsTableSortConfig;
  t: TFunction<['DefaultTranslations' | 'ProblemReportsTranslations']>;
};

const createStatusCountColumn = ({
  status,
  sortByKey,
  sortBy,
  sortOrder,
  sortConfig,
  t
}: StatusCountColumnConfig): ColumnsType<ProblemReportRow>[number] => ({
  title: sortConfig ? (
    <Tooltip title={getSortTooltip(sortByKey, sortBy, sortOrder, t)}>
      <span>{t(`ProblemReportsTranslations:${status}`)}</span>
    </Tooltip>
  ) : (
    t(`ProblemReportsTranslations:${status}`)
  ),
  dataIndex: 'reports',
  key: sortByKey,
  width: 120,
  sorter: !!sortConfig,
  sortOrder: sortConfig ? getSortOrder(sortByKey, sortBy, sortOrder) : undefined,
  showSorterTooltip: false,
  render: (reports: IReport[]) => {
    const count = reports.filter(r => r.status === status).length;
    if (status === EProblemReportStatus.NOT_REVIEWED && count >= 1) {
      return <Badge count={count} align={EAlignTypes.CENTER} justify={EJustifyTypes.START} />;
    }
    return <Counter>{count}</Counter>;
  }
});

export const getProblemReportsColumns = (
  t: TFunction<['DefaultTranslations' | 'ProblemReportsTranslations']>,
  tabStatus?: EProblemReportStatus[],
  sortConfig?: ProblemReportsTableSortConfig
): ColumnsType<ProblemReportRow> => {
  const { sortBy, sortOrder } = sortConfig || {};

  const baseColumns: ColumnsType<ProblemReportRow> = [
    {
      title: t('ProblemReportsTranslations:TYPE'),
      dataIndex: 'resource',
      width: 80,
      render: (resource: Pick<IActivity, '_id' | 'type'>) => <TypeCell resource={resource} />
    },
    {
      title: t('ProblemReportsTranslations:TITLE'),
      width: 580,
      dataIndex: 'resourceTitle'
    }
  ];

  const notReviewedCountColumn = createStatusCountColumn({
    status: EProblemReportStatus.NOT_REVIEWED,
    sortByKey: EProblemReportSortBy.NOT_REVIEWED_COUNT,
    sortBy,
    sortOrder,
    sortConfig,
    t
  });

  const inReviewCountColumn = createStatusCountColumn({
    status: EProblemReportStatus.IN_REVIEW,
    sortByKey: EProblemReportSortBy.IN_REVIEW_COUNT,
    sortBy,
    sortOrder,
    sortConfig,
    t
  });

  const reviewedCountColumn = createStatusCountColumn({
    status: EProblemReportStatus.REVIEWED,
    sortByKey: EProblemReportSortBy.REVIEWED_COUNT,
    sortBy,
    sortOrder,
    sortConfig,
    t
  });

  const totalReportsColumn: ColumnsType<ProblemReportRow>[number] = {
    title: sortConfig ? (
      <Tooltip title={getSortTooltip(EProblemReportSortBy.TOTAL_REPORTS, sortBy, sortOrder, t)}>
        <TotalReportsColumHeader>
          {t('ProblemReportsTranslations:TOTAL_REPORTS')}
        </TotalReportsColumHeader>
      </Tooltip>
    ) : (
      <TotalReportsColumHeader>
        {t('ProblemReportsTranslations:TOTAL_REPORTS')}
      </TotalReportsColumHeader>
    ),
    dataIndex: 'reports',
    key: EProblemReportSortBy.TOTAL_REPORTS,
    width: 150,
    sorter: !!sortConfig,
    sortOrder: sortConfig
      ? getSortOrder(EProblemReportSortBy.TOTAL_REPORTS, sortBy, sortOrder)
      : undefined,
    showSorterTooltip: false,
    render: (reports: IReport[]) => <span>{reports.length}</span>
  };

  const creationDateColumn: ColumnsType<ProblemReportRow>[number] = {
    title: sortConfig ? (
      <Tooltip title={getSortTooltip(EProblemReportSortBy.CREATION_DATE, sortBy, sortOrder, t)}>
        <span>{t('ProblemReportsTranslations:CREATION_DATE')}</span>
      </Tooltip>
    ) : (
      t('ProblemReportsTranslations:CREATION_DATE')
    ),
    dataIndex: 'reports',
    key: EProblemReportSortBy.CREATION_DATE,
    width: 130,
    sorter: !!sortConfig,
    sortOrder: sortConfig
      ? getSortOrder(EProblemReportSortBy.CREATION_DATE, sortBy, sortOrder)
      : undefined,
    showSorterTooltip: false,
    render: (reports: IReport[]) => {
      if (!reports?.length) {
        return <span>-</span>;
      }
      const firstReport = reports.reduce((oldest, report) =>
        new Date(report.createdAt) < new Date(oldest.createdAt) ? report : oldest
      );
      return <span>{formatRelativeDate(firstReport.createdAt, t)}</span>;
    }
  };

  const lastReportDateColumn: ColumnsType<ProblemReportRow>[number] = {
    title: t('ProblemReportsTranslations:LAST_REPORT_DATE'),
    dataIndex: 'reports',
    width: 180,
    render: (reports: IReport[]) => <LastReportDateCell reports={reports} />
  };

  const actionsColumn: ColumnsType<ProblemReportRow>[number] = {
    width: 100,
    dataIndex: '_id',
    title: t('ACTIONS'),
    align: 'center' as AlignType,
    render: (_id: string) => <MoreActions problemReportId={_id} />
  };

  const isOpenTab =
    tabStatus?.includes(EProblemReportStatus.NOT_REVIEWED) &&
    tabStatus?.includes(EProblemReportStatus.IN_REVIEW) &&
    !tabStatus?.includes(EProblemReportStatus.REVIEWED);

  const isReviewedTab =
    tabStatus?.includes(EProblemReportStatus.REVIEWED) && tabStatus?.length === 1;

  if (isOpenTab) {
    return [
      ...baseColumns,
      notReviewedCountColumn,
      inReviewCountColumn,
      lastReportDateColumn,
      actionsColumn
    ];
  }

  if (isReviewedTab) {
    return [...baseColumns, totalReportsColumn, creationDateColumn, actionsColumn];
  }

  return [
    ...baseColumns,
    notReviewedCountColumn,
    inReviewCountColumn,
    reviewedCountColumn,
    totalReportsColumn,
    creationDateColumn,
    actionsColumn
  ];
};
